import Input from '@veeva/corgix/input';
import { useContext } from 'react';
import PropTypes from 'prop-types';
import Icon from '@veeva/corgix/icon';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import OverlayContext from '../../containers/OverlayContext';

export default function SearchBoxInput({
    children,
    className,
    placeholder,
    inputRef,
    inputContainerRef,
    onSearchTextChange,
    onBlur,
    searchText,
    focusedNodeKeyValue,
    setFocusedNodeKeyValue,
    shouldUseMobileSearchOverlay,
    selectedValue,
    menuMapRef,
    onItemSelected,
    autoFocus,
    overlayType,
    showSearchResults,
    setShowSearchResults,
    setSearchParams,
}) {
    const { setOverlay } = useContext(OverlayContext);
    const handleSearchIconClick = () => inputRef.current.focus();
    const openSearchResults = () => {
        if (shouldUseMobileSearchOverlay) {
            setOverlay(overlayType);
        } else {
            setShowSearchResults(true);
        }
        setFocusedNodeKeyValue(selectedValue);
    };
    const handleSearchTextChange = (e) => {
        onSearchTextChange(e.target.value);
        setSearchParams('');
    };
    const handleInputFocus = () => openSearchResults();
    const handleInputBlur = () => {
        onBlur?.();
        if (!shouldUseMobileSearchOverlay) {
            setShowSearchResults(false);
        }
    };
    const handleInputClick = () => openSearchResults();

    const handleKeyDown = (e) => {
        switch (e.key) {
            case 'ArrowDown': {
                if (!showSearchResults && !shouldUseMobileSearchOverlay) {
                    openSearchResults();
                } else {
                    const newNode =
                        focusedNodeKeyValue === undefined
                            ? menuMapRef.current.getFirstNode()
                            : menuMapRef.current.getNextNode(focusedNodeKeyValue);
                    if (newNode) {
                        setFocusedNodeKeyValue(newNode.keyValue);
                    }
                }
                break;
            }
            case 'ArrowUp': {
                if (!showSearchResults && !shouldUseMobileSearchOverlay) {
                    openSearchResults();
                } else {
                    const newNode =
                        focusedNodeKeyValue === undefined
                            ? menuMapRef.current.getLastNode()
                            : menuMapRef.current.getPreviousNode(focusedNodeKeyValue);
                    if (newNode) {
                        setFocusedNodeKeyValue(newNode.keyValue);
                    }
                }
                break;
            }
            case 'Enter': {
                if (!showSearchResults && !shouldUseMobileSearchOverlay) {
                    openSearchResults();
                } else if (focusedNodeKeyValue && focusedNodeKeyValue !== 'not-found') {
                    if (focusedNodeKeyValue !== selectedValue) {
                        onItemSelected(focusedNodeKeyValue);
                        setSearchParams('');
                    }
                    if (!shouldUseMobileSearchOverlay) {
                        setShowSearchResults(false);
                    }
                }
                if (shouldUseMobileSearchOverlay) {
                    setOverlay(null);
                }
                break;
            }
            case 'Escape': {
                if (!shouldUseMobileSearchOverlay) {
                    setShowSearchResults(false);
                } else {
                    setOverlay(null);
                }
                break;
            }
            default:
                // Open search results when we start typing
                openSearchResults();
                break;
        }
    };

    return (
        <Input
            className={className}
            inputRef={inputRef}
            nodeRef={inputContainerRef}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onClick={handleInputClick}
            onBlur={shouldUseMobileSearchOverlay ? null : handleInputBlur}
            onChange={handleSearchTextChange}
            placeholder={placeholder}
            value={searchText || ''}
            autoFocus={autoFocus}
        >
            <Icon
                onClick={handleSearchIconClick}
                size="md"
                className="prepend"
                type={faSearch}
                title="Search"
            />
            {children}
        </Input>
    );
}
SearchBoxInput.propTypes = {
    autoFocus: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    focusedNodeKeyValue: PropTypes.string,
    inputContainerRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]),
    inputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]),
    menuMapRef: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]),
    onBlur: PropTypes.func,
    onItemSelected: PropTypes.func,
    onSearchTextChange: PropTypes.func.isRequired,
    overlayType: PropTypes.string,
    placeholder: PropTypes.string,
    searchText: PropTypes.string,
    selectedValue: PropTypes.string,
    setFocusedNodeKeyValue: PropTypes.func,
    setSearchParams: PropTypes.func,
    setShowSearchResults: PropTypes.func,
    shouldUseMobileSearchOverlay: PropTypes.bool,
    showSearchResults: PropTypes.bool,
};
